import { useCallback } from 'react';

type UseScrollToSectionOptions = {
  containerRef: React.RefObject<HTMLElement | null>;
  sectionRefs: React.RefObject<Map<string | number, HTMLElement>>;
  isProgrammaticScroll: React.RefObject<boolean>;
  scrollOffset?: number;
  axis?: 'x' | 'y';
};

export function useScrollToSection({
  containerRef,
  sectionRefs,
  isProgrammaticScroll,
  scrollOffset = 20,
  axis = 'y',
}: UseScrollToSectionOptions): { scrollToId: (id: string | number) => void } {
  const scrollToId = useCallback(
    (id: string | number): void => {
      const container = containerRef.current;
      const element = sectionRefs.current.get(id);

      if (!container || !element) return;

      isProgrammaticScroll.current = true;

      requestAnimationFrame((): void => {
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        if (axis === 'x') {
          const elementLeftRelativeToContainer = elementRect.left - containerRect.left + container.scrollLeft;
          container.scrollTo({ left: Math.max(0, elementLeftRelativeToContainer - scrollOffset), behavior: 'smooth' });
        } else {
          const elementTopRelativeToContainer = elementRect.top - containerRect.top + container.scrollTop;
          container.scrollTo({ top: Math.max(0, elementTopRelativeToContainer - scrollOffset), behavior: 'smooth' });
        }

        const timeoutId = setTimeout((): void => {
          isProgrammaticScroll.current = false;
        }, 1500);

        const handleScrollEnd = (): void => {
          clearTimeout(timeoutId);
          isProgrammaticScroll.current = false;
        };

        if ('onscrollend' in container) {
          container.addEventListener('scrollend', handleScrollEnd, { once: true });
        }
      });
    },
    [axis, containerRef, sectionRefs, isProgrammaticScroll, scrollOffset]
  );

  return { scrollToId };
}
